import React, { useState } from "react";
import {
	Target,
	Plus,
	Edit3,
	Trash2,
	Clock,
	ChevronDown,
	ChevronUp,
	Check,
	AlertCircle,
} from "lucide-react";
import CustomModal from "./CustomModal";

const GOAL_CATEGORIES = [
	"Strength",
	"Cardio",
	"Flexibility",
	// "Nutrition",
	// "Weight Loss",
	"Endurance",
	"Other",
];

const GOAL_STATUSES = [
	{ value: "in_progress", label: "In Progress", color: "#d15a36" },
	{ value: "achieved", label: "Achieved", color: "#28a745" },
	{ value: "missed", label: "Missed", color: "#dc3545" },
	{ value: "abandoned", label: "Abandoned", color: "#6c757d" },
];

const UserGoals = ({
	goals,
	goalDeadline,
	onAddGoal,
	onUpdateGoal,
	onDeleteGoal,
	loading,
}) => {
	const [showForm, setShowForm] = useState(false);
	const [editingGoal, setEditingGoal] = useState(null);
	const [expandedGoals, setExpandedGoals] = useState({});
	const [formData, setFormData] = useState({
		title: "",
		description: "",
		category: "",
		status: "in_progress",
	});
	const [modal, setModal] = useState({
		show: false,
		title: "",
		message: "",
		type: "info",
	});
	const [confirmModal, setConfirmModal] = useState({
		show: false,
		title: "",
		message: "",
		onConfirm: null,
	});

	const resetForm = () => {
		setFormData({
			title: "",
			description: "",
			category: "",
			status: "in_progress",
		});
		setEditingGoal(null);
		setShowForm(false);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!formData.title.trim()) {
			setModal({
				show: true,
				title: "Missing Title",
				message: "Please give your goal a title.",
				type: "warning",
			});
			return;
		}
		if (!formData.description.trim()) {
			setModal({
				show: true,
				title: "Missing Description",
				message: "Please describe your goal or challenge.",
				type: "warning",
			});
			return;
		}

		try {
			if (editingGoal) {
				await onUpdateGoal(editingGoal.id, formData);
			} else {
				await onAddGoal(formData);
			}
			resetForm();
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message || "Failed to save goal.",
				type: "error",
			});
		}
	};

	const startEdit = (goal) => {
		setFormData({
			title: goal.title,
			description: goal.description,
			category: goal.category || "",
			status: goal.status,
		});
		setEditingGoal(goal);
		setShowForm(true);
	};

	const handleDelete = (goal) => {
		setConfirmModal({
			show: true,
			title: "Delete Goal",
			message: `Are you sure you want to delete "${goal.title}"? This cannot be undone.`,
			onConfirm: async () => {
				setConfirmModal({
					show: false,
					title: "",
					message: "",
					onConfirm: null,
				});
				try {
					await onDeleteGoal(goal.id);
				} catch (error) {
					setModal({
						show: true,
						title: "Error",
						message: error.message || "Failed to delete goal.",
						type: "error",
					});
				}
			},
		});
	};

	const toggleExpand = (goalId) => {
		setExpandedGoals((prev) => ({ ...prev, [goalId]: !prev[goalId] }));
	};

	const getStatusInfo = (status) => {
		return GOAL_STATUSES.find((s) => s.value === status) || GOAL_STATUSES[0];
	};

	// Calculate days remaining
	const getDaysRemaining = () => {
		if (!goalDeadline?.deadlineDate) return null;
		const now = new Date();
		const deadline = new Date(goalDeadline.deadlineDate);
		const diffMs = deadline - now;
		const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
		return diffDays;
	};

	const daysRemaining = getDaysRemaining();

	return (
		<div>
			<div className="d-flex justify-content-between align-items-center mb-3">
				<h5 className="mb-0">
					<Target className="me-2" size={20} />
					My Goals
				</h5>
				<button
					className="btn btn-primary btn-sm"
					onClick={() => {
						if (showForm && !editingGoal) {
							resetForm();
						} else {
							resetForm();
							setShowForm(true);
						}
					}}
					disabled={loading}
				>
					{showForm && !editingGoal ? (
						"Cancel"
					) : (
						<>
							<Plus size={16} className="me-1" />
							New Goal
						</>
					)}
				</button>
			</div>

			{/* Deadline Banner */}
			{goalDeadline?.deadlineDate && (
				<div
					className="mb-3 p-3 d-flex align-items-center gap-2"
					style={{
						backgroundColor:
							daysRemaining <= 7
								? "rgba(220,53,69,0.15)"
								: "rgba(209,90,54,0.15)",
						borderRadius: "8px",
						border: `1px solid ${daysRemaining <= 7 ? "rgba(220,53,69,0.3)" : "rgba(209,90,54,0.3)"}`,
					}}
				>
					<Clock
						size={18}
						style={{ color: daysRemaining <= 7 ? "#dc3545" : "#d15a36" }}
					/>
					<div>
						<strong style={{ color: "#fff" }}>
							{goalDeadline.challengeName || "Current Challenge"}
						</strong>
						<span
							style={{ color: "rgba(255,255,255,0.7)", marginLeft: "0.5rem" }}
						>
							{daysRemaining > 0
								? `${daysRemaining} day${daysRemaining !== 1 ? "s" : ""} remaining`
								: daysRemaining === 0
									? "Deadline is today!"
									: `Ended ${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) !== 1 ? "s" : ""} ago`}
						</span>
						<small style={{ color: "rgba(255,255,255,0.5)", display: "block" }}>
							Deadline:{" "}
							{new Date(goalDeadline.deadlineDate).toLocaleDateString("en-GB", {
								day: "numeric",
								month: "long",
								year: "numeric",
							})}
						</small>
					</div>
				</div>
			)}

			{!goalDeadline?.deadlineDate && (
				<div
					className="mb-3 p-3 d-flex align-items-center gap-2"
					style={{
						backgroundColor: "rgba(108,117,125,0.15)",
						borderRadius: "8px",
						border: "1px solid rgba(108,117,125,0.3)",
					}}
				>
					<AlertCircle size={18} style={{ color: "#6c757d" }} />
					<span style={{ color: "rgba(255,255,255,0.7)" }}>
						No active challenge deadline set by your trainer yet.
					</span>
				</div>
			)}

			{/* Goal Form */}
			{showForm && (
				<div
					className="mb-4 p-3"
					style={{
						backgroundColor: "#2a2a2a",
						borderRadius: "8px",
						border: "1px solid #444",
					}}
				>
					<h6 style={{ color: "#d15a36", marginBottom: "1rem" }}>
						{editingGoal ? "Edit Goal" : "Set a New Goal"}
					</h6>
					<form onSubmit={handleSubmit}>
						<div className="row g-3">
							<div className="col-12 col-md-8">
								<label
									className="form-label small"
									style={{ color: "rgba(255,255,255,0.6)" }}
								>
									Title *
								</label>
								<input
									type="text"
									className="form-control"
									value={formData.title}
									onChange={(e) =>
										setFormData({ ...formData, title: e.target.value })
									}
									placeholder="e.g. Run 5k in under 25 minutes"
									disabled={loading}
									maxLength={100}
									style={{
										backgroundColor: "#1a1a1a",
										border: "1px solid #444",
										color: "#fff",
									}}
								/>
							</div>
							<div className="col-12 col-md-4">
								<label
									className="form-label small"
									style={{ color: "rgba(255,255,255,0.6)" }}
								>
									Category
								</label>
								<select
									className="form-select"
									value={formData.category}
									onChange={(e) =>
										setFormData({ ...formData, category: e.target.value })
									}
									disabled={loading}
									style={{
										backgroundColor: "#1a1a1a",
										border: "1px solid #444",
										color: "#fff",
									}}
								>
									<option value="">Select category</option>
									{GOAL_CATEGORIES.map((cat) => (
										<option key={cat} value={cat}>
											{cat}
										</option>
									))}
								</select>
							</div>
							<div className="col-12">
								<label
									className="form-label small"
									style={{ color: "rgba(255,255,255,0.6)" }}
								>
									Description *
								</label>
								<textarea
									className="form-control"
									rows="4"
									value={formData.description}
									onChange={(e) =>
										setFormData({ ...formData, description: e.target.value })
									}
									placeholder="Describe your goal or challenge in detail. What do you want to achieve? What's your plan?"
									disabled={loading}
									maxLength={1000}
									style={{
										backgroundColor: "#1a1a1a",
										border: "1px solid #444",
										color: "#fff",
									}}
								/>
								<small style={{ color: "rgba(255,255,255,0.4)" }}>
									{formData.description.length}/1000
								</small>
							</div>
							{editingGoal && (
								<div className="col-12 col-md-4">
									<label
										className="form-label small"
										style={{ color: "rgba(255,255,255,0.6)" }}
									>
										Status
									</label>
									<select
										className="form-select"
										value={formData.status}
										onChange={(e) =>
											setFormData({ ...formData, status: e.target.value })
										}
										disabled={loading}
										style={{
											backgroundColor: "#1a1a1a",
											border: "1px solid #444",
											color: "#fff",
										}}
									>
										{GOAL_STATUSES.map((s) => (
											<option key={s.value} value={s.value}>
												{s.label}
											</option>
										))}
									</select>
								</div>
							)}
							<div className="col-12">
								<div className="d-flex gap-2">
									<button
										type="submit"
										className="btn btn-success"
										disabled={loading}
									>
										{loading
											? "Saving..."
											: editingGoal
												? "Update Goal"
												: "Save Goal"}
									</button>
									<button
										type="button"
										className="btn btn-secondary"
										onClick={resetForm}
										disabled={loading}
									>
										Cancel
									</button>
								</div>
							</div>
						</div>
					</form>
				</div>
			)}

			{/* Goals List */}
			{goals.length === 0 ? (
				<div className="text-center py-5">
					<Target
						size={48}
						style={{ color: "rgba(255,255,255,0.2)", marginBottom: "1rem" }}
					/>
					<p style={{ color: "rgba(255,255,255,0.5)" }}>
						No goals set yet. Create your first goal to start tracking your
						progress!
					</p>
				</div>
			) : (
				<div>
					{goals.map((goal) => {
						const isExpanded = expandedGoals[goal.id];
						const statusInfo = getStatusInfo(goal.status);

						return (
							<div
								key={goal.id}
								className="mb-3"
								style={{
									borderRadius: "8px",
									overflow: "hidden",
									border: isExpanded ? "1px solid #d15a36" : "1px solid #333",
									backgroundColor: "#1a1a1a",
									transition: "border-color 0.2s ease",
								}}
							>
								{/* Header */}
								<div
									className="p-3"
									onClick={() => toggleExpand(goal.id)}
									style={{
										cursor: "pointer",
										borderBottom: isExpanded ? "2px solid #d15a36" : "none",
									}}
								>
									<div className="d-flex justify-content-between align-items-start gap-2 mb-1">
										<div style={{ flex: 1, minWidth: 0 }}>
											<h6
												className="mb-0"
												style={{
													color: "#fff",
													wordBreak: "break-word",
													fontSize: "1rem",
												}}
											>
												{goal.title}
											</h6>
										</div>
										<div className="d-flex align-items-center gap-2 flex-shrink-0">
											{isExpanded ? (
												<ChevronUp size={18} style={{ color: "#d15a36" }} />
											) : (
												<ChevronDown
													size={18}
													style={{ color: "rgba(255,255,255,0.5)" }}
												/>
											)}
										</div>
									</div>

									{/* Meta row */}
									<div className="d-flex flex-wrap align-items-center gap-2">
										<span
											className="badge"
											style={{
												backgroundColor: statusInfo.color,
												fontSize: "0.7rem",
											}}
										>
											{statusInfo.label}
										</span>
										{goal.category && (
											<span
												className="badge"
												style={{
													backgroundColor: "#333",
													color: "#e1d0bb",
													fontSize: "0.7rem",
												}}
											>
												{goal.category}
											</span>
										)}
										<small style={{ color: "rgba(255,255,255,0.45)" }}>
											{new Date(goal.createdAt).toLocaleDateString("en-GB")}
										</small>
									</div>
								</div>

								{/* Expanded content */}
								{isExpanded && (
									<div className="p-3" style={{ overflow: "hidden" }}>
										<p
											style={{
												color: "rgba(255,255,255,0.7)",
												fontSize: "0.9rem",
												lineHeight: 1.6,
												wordBreak: "break-word",
												whiteSpace: "pre-wrap",
												marginBottom: "1rem",
											}}
										>
											{goal.description}
										</p>

										{/* Progress Notes */}
										{goal.progressNotes && goal.progressNotes.length > 0 && (
											<div className="mb-3">
												<h6
													style={{
														color: "#d15a36",
														fontSize: "0.85rem",
														marginBottom: "0.5rem",
													}}
												>
													Progress Notes
												</h6>
												{goal.progressNotes.map((note, index) => (
													<div
														key={index}
														className="mb-2 p-2"
														style={{
															backgroundColor: "#2a2a2a",
															borderRadius: "6px",
															borderLeft: "3px solid #d15a36",
														}}
													>
														<small style={{ color: "rgba(255,255,255,0.5)" }}>
															{new Date(note.date).toLocaleDateString("en-GB")}
														</small>
														<p
															className="mb-0"
															style={{
																color: "rgba(255,255,255,0.7)",
																fontSize: "0.85rem",
															}}
														>
															{note.text}
														</p>
													</div>
												))}
											</div>
										)}

										{/* Actions */}
										<div className="d-flex gap-2">
											<button
												className="btn btn-sm"
												onClick={(e) => {
													e.stopPropagation();
													startEdit(goal);
												}}
												disabled={loading}
												style={{
													backgroundColor: "rgba(209,90,54,0.15)",
													color: "#d15a36",
													border: "none",
												}}
											>
												<Edit3 size={14} className="me-1" />
												Edit
											</button>
											<button
												className="btn btn-sm"
												onClick={(e) => {
													e.stopPropagation();
													handleDelete(goal);
												}}
												disabled={loading}
												style={{
													backgroundColor: "rgba(220,53,69,0.15)",
													color: "#dc3545",
													border: "none",
												}}
											>
												<Trash2 size={14} className="me-1" />
												Delete
											</button>
										</div>
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}

			<CustomModal
				show={modal.show}
				title={modal.title}
				message={modal.message}
				type={modal.type}
				onClose={() => setModal({ ...modal, show: false })}
			/>
			{/* Confirm Modal */}
			<CustomModal
				show={confirmModal.show}
				title={confirmModal.title}
				message={confirmModal.message}
				type="delete"
				onClose={() => setConfirmModal({ ...confirmModal, show: false })}
				onConfirm={confirmModal.onConfirm}
				confirmText="Delete"
			/>
		</div>
	);
};

export default UserGoals;
